import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button, Alert } from '@mui/material';
import { useNotify, useRefresh } from 'react-admin';
import { ArrowLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '../../../services/supabase';
import { CopyableId } from '../../../shared/components/CopyableId';
import { UserListenedStoriesList } from '../components/UserListenedStoriesList';
import { UserExposuresList } from '../components/UserExposuresList';
import { UserSessionList } from '../components/UserSessionList';
import { UserEventsList } from '../components/UserEventsList';
import { UserInsertsList } from '../components/UserInsertsList';
import { UserRecommendationsTab } from '../components/UserRecommendationsTab';
import { UserInterestsTab } from '../components/UserInterestsTab';

interface AuthUser {
  id: string;
  email?: string;
  displayName?: string;
}

interface UserProfile {
  firstSeen?: string;
  lastSeen?: string;
  storiesPlayed?: number;
  sessions?: number | null;
}

const relativeTime = (dateStr?: string | null) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return 'now';
  return formatDistanceToNow(date, { addSuffix: true })
    .replace(/^about /, '')
    .replace(/^over /, '')
    .replace(/^almost /, '');
};

export const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();

  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<UserProfile>({});
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Fetch auth user details and user_profiles in parallel
      const [authResult, profileResult] = await Promise.all([
        supabase.functions.invoke('get-users', {
          body: { userIds: [id] },
        }),
        supabase
          .from('user_profiles')
          .select('first_seen, last_seen, stories_played, sessions')
          .eq('user_id', id)
          .single(),
      ]);

      // Auth user
      const { data: userData, error: userError } = authResult;
      if (userError || !userData?.users?.[0]) {
        setUser({ id });
      } else {
        const u = userData.users[0];
        setUser({
          id: u.id,
          email: u.email || undefined,
          displayName: u.displayName || undefined,
        });
      }

      // User profile
      if (profileResult.data) {
        setProfile({
          firstSeen: profileResult.data.first_seen,
          lastSeen: profileResult.data.last_seen,
          storiesPlayed: profileResult.data.stories_played,
          sessions: profileResult.data.sessions,
        });
      }
    } catch {
      notify('Error fetching user data', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    fetchData();

    // Set up realtime subscriptions to refresh lists
    const listenedChannel = supabase
      .channel('user-listened-stories-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_listened_stories',
          filter: `user_id=eq.${id}`,
        },
        () => {
          setRefreshKey(prev => prev + 1);
          refresh(); // Invalidate React-Admin cache
        }
      )
      .subscribe();

    const exposuresChannel = supabase
      .channel('exposures-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'exposures',
          filter: `user_id=eq.${id}`,
        },
        () => {
          setRefreshKey(prev => prev + 1);
          refresh(); // Invalidate React-Admin cache
        }
      )
      .subscribe();

    const eventsChannel = supabase
      .channel('user-events-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_events',
          filter: `user_id=eq.${id}`,
        },
        () => {
          setRefreshKey(prev => prev + 1);
          refresh(); // Invalidate React-Admin cache
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(listenedChannel);
      supabase.removeChannel(exposuresChannel);
      supabase.removeChannel(eventsChannel);
    };
  }, [id, notify, fetchData, refresh]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading user data...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">User not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/users')}
          sx={{ mb: 2 }}
        >
          Back to Users
        </Button>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          {user.displayName || user.email || '(no email)'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <CopyableId id={user.id} variant="caption" />
          {user.displayName && user.email && (
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          )}
          {profile.firstSeen && (
            <Typography variant="caption" color="text.secondary">
              Joined {relativeTime(profile.firstSeen)}
            </Typography>
          )}
          {profile.lastSeen && (
            <Typography variant="caption" color="text.secondary">
              Last seen {relativeTime(profile.lastSeen)}
            </Typography>
          )}
          {profile.storiesPlayed != null && (
            <Typography variant="caption" color="text.secondary">
              {profile.storiesPlayed} drops played
            </Typography>
          )}
          {profile.sessions != null && (
            <Typography variant="caption" color="text.secondary">
              {profile.sessions} session{profile.sessions === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label="Listened stories" />
        <Tab label="Exposures" />
        <Tab label="Session" />
        <Tab label="Events" />
        <Tab label="Inserts" />
        <Tab label="Recommendations" />
        <Tab label="Interests" />
      </Tabs>

      {/* Listened Stories Tab */}
      {activeTab === 0 && (
        <UserListenedStoriesList key={`listened-${refreshKey}`} userId={id!} />
      )}

      {/* Exposures Tab */}
      {activeTab === 1 && (
        <UserExposuresList key={`exposures-${refreshKey}`} userId={id!} />
      )}

      {/* Session Tab */}
      {activeTab === 2 && (
        <UserSessionList
          key={`session-${refreshKey}`}
          userId={id!}
          refreshKey={refreshKey}
        />
      )}

      {/* Events Tab */}
      {activeTab === 3 && (
        <UserEventsList key={`events-${refreshKey}`} userId={id!} />
      )}

      {/* Inserts Tab */}
      {activeTab === 4 && (
        <UserInsertsList
          key={`inserts-${refreshKey}`}
          userId={id!}
          refreshKey={refreshKey}
        />
      )}

      {/* Recommendations Tab */}
      {activeTab === 5 && (
        <UserRecommendationsTab userId={id!} />
      )}

      {/* Interests Tab */}
      {activeTab === 6 && (
        <UserInterestsTab userId={id!} />
      )}
    </Box>
  );
};
